import { Children, isValidElement, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

export interface FilterBarProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Free-form left-side search input (legacy slot). Rendered above filter rows.
   */
  search?: ReactNode;
  /**
   * Body of the dense filter grid. Wrap each label/control pair with
   * `<FilterField label="...">control</FilterField>` so labels align in a
   * left column (60px) and inputs stretch right.
   */
  filters?: ReactNode;
  /**
   * Right-aligned actions (e.g. "검색", "초기화"). Sticky right.
   */
  actions?: ReactNode;
  /**
   * Dense Korean-style layout — label (60px) + inline input, rows wrap.
   * Default true (R1 baseline 카페24 정합).
   */
  dense?: boolean;
}

export interface FilterFieldProps {
  label: ReactNode;
  /** Override the default 72px label column for long labels. */
  labelWidth?: number | string;
  children: ReactNode;
  className?: string;
}

/**
 * Single label/control pair in a dense FilterBar row. Keeps label width
 * consistent so multiple rows stack into a visual grid.
 *
 * Default column = 72px: at 12px the Korean (loos/Pretendard) glyph runs ~12px
 * each, so 4-char labels (결제수단/주문상태/배송방법…) measure 48px and the common
 * 6-char labels (마지막로그인=72px) fit on one line with margin. `nowrap` keeps any
 * longer label on a single line (it extends past the column rather than wrapping
 * into two lines that misalign the row); pass `labelWidth` to widen if needed.
 */
export const FilterField = ({ label, labelWidth = 72, children, className }: FilterFieldProps) => (
  <div className={cn('flex items-center gap-2', className)}>
    <span
      className="shrink-0 whitespace-nowrap text-[12px] font-medium"
      style={{ width: labelWidth, color: 'var(--content-muted)' }}
    >
      {label}
    </span>
    <div className="min-w-[160px]">{children}</div>
  </div>
);

const hasFilterFieldChildren = (filters: ReactNode): boolean => {
  let found = false;
  Children.forEach(filters, (child) => {
    if (isValidElement(child) && child.type === FilterField) {
      found = true;
    }
  });
  return found;
};

export const FilterBar = ({
  search,
  filters,
  actions,
  children,
  className,
  dense = true,
  ...props
}: FilterBarProps) => {
  if (!dense) {
    return (
      <div
        className={cn('flex flex-wrap items-center gap-2 px-4 py-3', className)}
        style={{
          background: 'var(--surface-0)',
          border: '1px solid var(--divider-strong)',
          borderRadius: 'var(--radius-lg)',
        }}
        role="search"
        {...props}
      >
        {search ? <div className="min-w-[220px] flex-1">{search}</div> : null}
        {filters ? <div className="flex flex-wrap items-center gap-2">{filters}</div> : null}
        {children}
        {actions ? <div className="ml-auto flex items-center gap-2">{actions}</div> : null}
      </div>
    );
  }

  const usesFilterField = hasFilterFieldChildren(filters);

  return (
    <div
      className={cn(className)}
      style={{
        background: 'var(--surface-0)',
        border: '1px solid var(--divider-strong)',
        borderRadius: 'var(--radius-lg)',
        padding: '12px 16px',
      }}
      role="search"
      {...props}
    >
      {search ? <div className="mb-2 max-w-[360px]">{search}</div> : null}

      <div className="flex items-start justify-between gap-3">
        <div
          className={cn(
            'flex-1',
            usesFilterField
              ? 'grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
              : 'flex flex-wrap items-center gap-2',
          )}
        >
          {filters}
          {children}
        </div>

        {actions ? (
          <div
            className="flex shrink-0 items-center gap-2"
            style={{ alignSelf: 'flex-start' }}
          >
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
};
